import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Home, ChevronRight, Search, Plus, Edit2, Trash2, Filter, Plane, Ship, Package, Upload, Download, X, CheckCircle, AlertCircle } from 'lucide-react';

const CARRIER_TYPES = ['AIRLINE', 'SHIPPING_LINE', 'COURIER'];
const TYPE_ICONS = { AIRLINE: Plane, SHIPPING_LINE: Ship, COURIER: Package };
const TYPE_COLORS = {
  AIRLINE: 'bg-sky-100 text-sky-700',
  SHIPPING_LINE: 'bg-blue-100 text-blue-700',
  COURIER: 'bg-amber-100 text-amber-700',
};

const CARRIER_CSV_TEMPLATE = `carrier_name,carrier_type,carrier_prefix_code,carrier_code,country,contact_person,phone,email,website,remarks
Air India,AIRLINE,AI,AI001,India,Contact Person,+91-9876543210,contact@airindia.com,,
Maersk Line,SHIPPING_LINE,MAE,MAE001,Denmark,,,,,
BlueDart,COURIER,,,India,,,,,
`;

const CarrierList = () => {
  const navigate = useNavigate();
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const fileInputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}` };
  }, []);

  const fetchCarriers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterType) params.append('carrier_type', filterType);
      if (filterActive !== '') params.append('is_active', filterActive);
      const response = await fetch(`${backendUrl}/api/carriers?${params.toString()}`, { headers: getAuthHeaders() });
      if (response.ok) setCarriers(await response.json());
    } catch (error) {
      console.error('Error fetching carriers:', error);
    } finally { setLoading(false); }
  }, [backendUrl, getAuthHeaders, search, filterType, filterActive]);

  useEffect(() => { fetchCarriers(); }, [fetchCarriers]);

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this carrier?')) return;
    try {
      const response = await fetch(`${backendUrl}/api/carriers/${id}`, { method: 'DELETE', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } });
      if (response.ok) fetchCarriers();
    } catch (error) { console.error('Error:', error); }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CARRIER_CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'carrier_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    setCsvResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${backendUrl}/api/carriers/bulk-import`, {
        method: 'POST', headers: getAuthHeaders(), body: formData
      });
      const data = await res.json();
      if (!res.ok) { setCsvResult({ error: data.detail || 'Upload failed' }); return; }
      setCsvResult(data);
      if (data.added > 0) fetchCarriers();
    } catch { setCsvResult({ error: 'Upload failed. Please try again.' }); }
    finally { setCsvUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  return (
    <div className="space-y-6" data-testid="carrier-list-page">
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <Home className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/')}>Home</span>
        <ChevronRight className="w-4 h-4" />
        <span className="cursor-pointer hover:text-accent-blue" onClick={() => navigate('/settings')}>Settings</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-text-dark font-medium">Carrier Master</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-dark font-manrope">Carrier Master</h1>
          <p className="text-sm text-text-secondary font-ibm mt-1">{carriers.length} carriers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setShowCsvModal(true); setCsvResult(null); }} data-testid="upload-csv-carrier-btn" className="border-gray-200">
            <Upload className="w-4 h-4 mr-2" /> Upload CSV
          </Button>
          <Button onClick={() => navigate('/settings/carriers/add')} data-testid="add-carrier-btn" className="bg-accent-blue hover:bg-accent-blue/90">
            <Plus className="w-4 h-4 mr-2" /> Add Carrier
          </Button>
        </div>
      </div>

      {/* CSV Upload Modal */}
      {showCsvModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-text-dark font-manrope">Import Carriers from CSV</h2>
              <button onClick={() => setShowCsvModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-blue-50 rounded-lg p-4 text-sm font-ibm text-blue-800">
                <p className="font-semibold mb-1">Required columns:</p>
                <p className="font-mono text-xs">carrier_name, carrier_type</p>
                <p className="mt-1 font-semibold">Optional:</p>
                <p className="font-mono text-xs">carrier_prefix_code, carrier_code, country, contact_person, phone, email, website, remarks</p>
                <p className="mt-1 text-xs">Valid types: AIRLINE, SHIPPING_LINE, COURIER</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate} className="w-full border-dashed border-gray-300">
                <Download className="w-4 h-4 mr-2" /> Download Template CSV
              </Button>
              <div>
                <label className="block text-sm font-medium text-text-dark font-ibm mb-2">Select CSV file</label>
                <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvUpload} disabled={csvUploading}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-accent-blue/10 file:text-accent-blue hover:file:bg-accent-blue/20 cursor-pointer" />
              </div>
              {csvUploading && <p className="text-sm text-text-secondary font-ibm text-center py-2">Uploading...</p>}
              {csvResult && !csvResult.error && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-1">
                  <div className="flex items-center gap-2 text-green-700 font-semibold font-ibm text-sm">
                    <CheckCircle className="w-4 h-4" /> Import Complete
                  </div>
                  <p className="text-sm font-ibm text-green-700">{csvResult.added} added · {csvResult.skipped} skipped (duplicates)</p>
                  {csvResult.errors?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {csvResult.errors.map((e, i) => <p key={i} className="text-xs text-red-600 font-ibm">{e}</p>)}
                    </div>
                  )}
                </div>
              )}
              {csvResult?.error && (
                <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 font-ibm">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {csvResult.error}
                </div>
              )}
            </div>
            <div className="flex justify-end p-5 border-t border-gray-100">
              <Button variant="outline" onClick={() => setShowCsvModal(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-text-secondary" />
          <span className="text-sm font-medium text-text-dark font-ibm">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input data-testid="carrier-search" placeholder="Search name, prefix, code..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 h-9 text-sm border-gray-200" />
          </div>
          <Select value={filterType || "all"} onValueChange={v => setFilterType(v === "all" ? '' : v)}>
            <SelectTrigger data-testid="filter-carrier-type" className="h-9 text-sm"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {CARRIER_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterActive === '' ? "all" : filterActive} onValueChange={v => setFilterActive(v === "all" ? '' : v)}>
            <SelectTrigger data-testid="filter-carrier-active" className="h-9 text-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="carrier-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                {['Carrier Name', 'Type', 'Prefix Code', 'Carrier Code', 'Country', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary font-ibm uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-text-secondary font-ibm">Loading...</td></tr>
              ) : carriers.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-text-secondary font-ibm">No carriers found</td></tr>
              ) : (
                carriers.map(c => {
                  const Icon = TYPE_ICONS[c.carrier_type] || Package;
                  return (
                    <tr key={c.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`carrier-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-text-secondary shrink-0" />
                          <span className="font-medium text-sm text-text-dark font-ibm">{c.carrier_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[c.carrier_type] || 'bg-gray-100 text-gray-700'}`}>
                          {c.carrier_type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-dark font-ibm font-mono">{c.carrier_prefix_code || '—'}</td>
                      <td className="px-4 py-3 text-sm text-text-dark font-ibm font-mono">{c.carrier_code || '—'}</td>
                      <td className="px-4 py-3 text-sm text-text-dark font-ibm">{c.country || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-carrier-${c.id}`} onClick={() => navigate(`/settings/carriers/edit/${c.id}`)}>
                            <Edit2 className="w-3.5 h-3.5 text-text-secondary" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`delete-carrier-${c.id}`} onClick={() => handleDelete(c.id)}>
                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default CarrierList;
